import React from 'react';
import {Box, Grid, TextField, Typography, Autocomplete, Button} from "@mui/material";
import NumberFormat from "react-number-format";
import swal from "sweetalert";
import { PuffLoader } from "react-spinners";
import { css } from "@emotion/react";
import {getAllWellnessTopic, createWellnessAttribute} from '../../../utilityFunctions/WellnessUtil';
import Colors from "../../../components/ui/Colors";
import { loaderAnimation } from '../../../Api';
import { useHistory } from "react-router-dom";

const cssLoader = css`
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const attributeRequest = {
    name: "",
    topicId: "",
};

/**
 * CreateWellnessAttribute component provides an interface for creating new wellness attributes.
 * Allows administrators to select a topic and create a new attribute with a question and group number.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.loginUser - The logged-in admin user with authentication token
 * @returns {JSX.Element} The CreateWellnessAttribute component
 */
export default function CreateWellnessAttribute(props) {

    const {loginUser} = props;

    let history = useHistory();

    const [loading, setLoading] = React.useState(false);

    const [wellnessTopics, setWellnessTopics] = React.useState(null);

    React.useEffect(() => {
        initialValues();
    }, []);

    /**
     * Fetches all wellness topics from the server and updates the state.
     * Shows an error message if the operation fails.
     * 
     * @async
     * @returns {Promise<void>}
     */
    const initialValues = async () => {
        setLoading(true);
        const response = await getAllWellnessTopic(loginUser.jwtToken);
        setLoading(false);

        if(!response){
            swal({
                title: "Error",
                text: "Failed to get wellness topics",
                icon: "error",
                button: "OK",
            });
            return;
        }

        setWellnessTopics(response);
    }

    const [wellnessQuestion, setWellnessQuestion] = React.useState("");
    const [wellnessTopic, setWellnessTopic] = React.useState(null);
    const [questionGroup, setQuestionGroup] = React.useState("");

    /**
     * Handles the selection of a wellness topic from the dropdown.
     * Updates the state and request object with the selected topic's ID.
     * 
     * @param {Event} e - The event object
     * @param {Object} value - The selected topic object
     * @returns {void}
     */
    const topicChange = (e, value) => {
        if(!value){
            return;
        }

        setWellnessTopic(value);
        attributeRequest.topicId = value.id;
    };

    /**
     * Updates the wellness question text in both state and request object.
     * 
     * @param {Event} e - The change event from the text field
     * @returns {void}
     */
    const changeWellnessQuestion = (e) => {
        setWellnessQuestion(e.target.value);
        attributeRequest.name = e.target.value;
    };

    /**
     * Updates the question group number in both state and request object.
     * 
     * @param {Object} values - The NumberFormat values object containing the new group number
     * @returns {void}
     */
    const questionGroupChange = (values) => {
        setQuestionGroup(values.floatValue);
        attributeRequest.questionGroup = values.floatValue;
    };

    /**
     * Submits the new wellness attribute to the server.
     * Shows success or error messages based on the operation result.
     * Redirects to the handle_profile page on success.
     * 
     * @async
     * @returns {Promise<void>}
     */
    const submitWellnessQuestion = async () => {
        setLoading(true);
        const response = await createWellnessAttribute(attributeRequest, loginUser.jwtToken);
        setLoading(false);

        if(!response){
            swal({
                title: "Error",
                text: "Failed to create wellness attribute",
                icon: "error",
                button: "OK",
            });
            return;
        }

        swal({
            title: "Success",
            text: "Wellness attribute created successfully",
            icon: "success",
            button: "OK",
        }).then(() => {
            history.push({
                pathname: "/handle_profile",
            });
        });
    };

    return (
        <Box sx={{display: "flex", justifyContent: "center", width: "100%", height: "100%"}}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "95%",
                    height: "90%",
                    textAlign: "center",
                    marginTop: {xs: "1.3rem", sm: 0},
                }}
            >
            {loading ? loaderAnimation:
                <Grid container direction="row" spacing={2}>
                {wellnessTopics &&
                    <>
                        <Grid item xs={12} sm={12} md={12} sx={{display: "flex", justifyContent: "center"}}>
                            <Autocomplete
                                sx={{width: {xs: "90%", sm: "80%", md: "60%"}}}
                                options={wellnessTopics}
                                getOptionLabel={(option) => option.name}
                                clearOnEscape
                                autoComplete = {true}
                                noOptionsText = {"topic not found"}
                                fullWidth
                                onChange={topicChange}
                                renderInput={(params) => (
                                    <TextField  {...params} label="Topic" variant="standard" />
                                )}
                            />
                        </Grid>
                        {wellnessTopic &&
                            <>
                                <Grid item xs={12} sm={12} md={12} sx={{marginTop: "1rem"}}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth={true}
                                        multiline
                                        rows={3}
                                        label={<Typography variant="body2">Question</Typography>}
                                        value={wellnessQuestion}
                                        onChange={(e)=> changeWellnessQuestion(e)}
                                        autoComplete="off"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12} sx={{display: "flex", justifyContent: "center"}}>
                                    <NumberFormat
                                        variant="standard"
                                        style={{width: "40%", height: "50%"}}
                                        customInput={TextField}
                                        type="text"
                                        label={<Typography variant="body2">Group</Typography>}
                                        thousandSeparator={'.'}
                                        decimalSeparator={','}
                                        autoComplete="off"
                                        value={questionGroup}
                                        onValueChange={(values) => {
                                            questionGroupChange(values);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <Button onClick={submitWellnessQuestion} variant="contained" sx={{backgroundColor: Colors.primary, marginTop: "2rem",}}>
                                        Submit
                                    </Button>
                                </Grid>
                            </>
                        }
                    </>
                }
                </Grid>
            }
            </Box>
        </Box>
    )
}
